import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { RouterService } from '../router-service';

@Injectable({ providedIn: 'root' })
export class UIStateService<T> implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	private readonly _uiState: BehaviorSubject<Partial<T>> = new BehaviorSubject(<Partial<T>>{});
	public readonly uiState$ = this._uiState.asObservable();

	private readonly _uiStateDeleted: BehaviorSubject<string> = new BehaviorSubject(<string>{});
	public readonly uiStateDeleted$ = this._uiStateDeleted.asObservable();

	private readonly uiStateMap: Map<string, Partial<T>> = new Map();

	private currentRouteUiStateIdentifier = '';

	constructor(private readonly routerService: RouterService) {
		this.routerService.url$.pipe(takeUntil(this.destroy$)).subscribe((url) => {
			this.currentRouteUiStateIdentifier = this.buildIdentifierForCurrentRoute(url);
			const routeUiState = this.uiStateMap.get(this.currentRouteUiStateIdentifier);
			if (!routeUiState) {
				this.uiStateMap.set(this.currentRouteUiStateIdentifier, <T>{});
			}

			this._uiState.next(routeUiState ?? <T>{});
		});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
	public updateOrCreateSlice(state: Partial<T>, identifier?: string): void {
		identifier = identifier ?? this.currentRouteUiStateIdentifier;

		this.uiStateMap.set(identifier, {
			...(<T>{}),
			...this.uiStateMap.get(identifier),
			...state
		});

		const routeUiState = this.uiStateMap.get(identifier);
		if (routeUiState) {
			this._uiState.next(routeUiState);
		}
	}

	public deleteSlice(identifier: string): void {
		this.uiStateMap.delete(identifier);
		this._uiStateDeleted.next(identifier);
	}

	public snapshot(identifier?: string): Partial<T> {
		identifier = identifier ?? this.currentRouteUiStateIdentifier;

		return this.uiStateMap.get(identifier) ?? <T>{};
	}

	public getCurrentRouteUiStateIdentifier(): string {
		return this.currentRouteUiStateIdentifier;
	}

	public getMatchingIdentifiers(identifierEndsWithString: string): string[] {
		const matches: string[] = [];
		this.uiStateMap.forEach((_, identifer) => {
			if (identifer.endsWith(identifierEndsWithString)) {
				matches.push(identifer);
			}
		});

		return matches;
	}

	public updateExistingSlice(identifier: string, stateUpdate: Partial<T>): void {
		if (!this.uiStateMap.has(identifier)) {
			return;
		}

		this.uiStateMap.set(identifier, {
			...this.uiStateMap.get(identifier),
			...stateUpdate
		});
	}

	private buildIdentifierForCurrentRoute(url: string[]): string {
		if (url.join('/').match(/room\/([\w\-%=]{1,})\/routing/)) {
			// Use the same identifier for all routes within /room/:id/routing (e.g. room presets, deployment)
			return url.slice(0, 3).join('');
		}
		if (url.join('/').match(/room\/([\w\-%=]{1,})\/coverage-map/)) {
			// Use the same identifier for all routes within /room/:id/coverage-map (e.g. room presets, deployment)
			return url.slice(0, 3).join('');
		}
		return url.join('');
	}
}
